import { inject, Injectable } from '@angular/core';
import { ChangeTyped, Query, SearchResult } from '@damofire/data-commons';
import { Observable } from 'rxjs';
import { Asset, Column, System2d, Upright } from '../asset/asset.dto';
import { FirebaseAssetService } from '../asset/firebase-asset.service';
import { FirebaseObservableAssetService } from '../asset/firebase-asset-observable.service';
import { TypedDesQuery } from '../des.dto';
import { FirebaseIssueService } from '../issue/firebase-issue.service';
import { FirebaseObservableIssueService } from '../issue/firebase-issue-observable.service';
import { FirebaseObservableIssueMediaService } from '../issue/firebase-issue-media-observable.service';
import { Issue, IssueMedia } from '../issue/issue.dto';
import { FirebaseWarehouseLayoutTextService } from '../layout/firebase-warehouse-layout-text.service';
import { FirebaseWarehouseOutlineService } from '../layout/firebase-warehouse-outline.service';
import { FirebaseObservableNoteService } from '../note/firebase-note-observable.service';
import { Note } from '../note/note.dto';
import { FirebaseSystemDrawingService } from '../system/firebase-system.service';
import { FirebaseUprightService } from '../upright/firebase-upright.service';
import { LayoutTextSegment, WarehouseOutlineSegment } from '../warehouse/warehouse.dto';
import { FirebaseWarehouseService } from '../warehouse/firebase-warehouse.service';
import { WarehouseStateSnapshotPayload } from '../snapshot/warehouse-state-snapshot.dto';
import { HistoricalViewState } from './historical-view-state';
import { fromHistory, readFromHistory, reloadLayoutFromHistory, snapshotReads } from './history-aware.reads';

/**
 * Read-through wrappers that serve the loaded snapshot instead of Firestore while the user is
 * viewing a historical warehouse state, and delegate to the live implementation otherwise.
 *
 * Each subclasses the Firebase implementation and overrides only its reads, so writes, media
 * uploads and every other method keep working exactly as before — a historical view is a lens on
 * reads, not a different service. The snapshot side of each read lives in history-aware.reads, shared
 * with the Idb wrappers used by offline builds.
 *
 * Declaring no constructor is deliberate: Angular inherits the parent's DI metadata, so these stay
 * immune to constructor churn in the services they wrap. New dependencies come from inject().
 *
 * Registered only in the online branch and only behind the warehouseStateSnapshots flag, so in
 * production the plain Firebase implementations are used.
 */

@Injectable()
export class HistoryAwareIssueService extends FirebaseIssueService {
  private readonly history = inject(HistoricalViewState);

  override search(divisionId: string, query: TypedDesQuery): Promise<SearchResult<Issue>> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.issueSearch(payload, query),
      () => super.search(divisionId, query),
    );
  }

  override get(divisionId: string, id: string): Promise<Issue> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.issue(payload, id),
      () => super.get(divisionId, id),
    );
  }
}

@Injectable()
export class HistoryAwareObservableIssueService extends FirebaseObservableIssueService {
  private readonly history = inject(HistoricalViewState);

  protected override observeOpen(divisionId: string, section?: string): Observable<ChangeTyped<Issue>[]> {
    // Waits for an in-flight payload instead of flashing live issues.
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.openIssues(payload, section),
      () => super.observeOpen(divisionId, section),
    );
  }

  protected override observeByInspection(divisionId: string, inspectionId: string): Observable<ChangeTyped<Issue>[]> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.inspectionIssues(payload, inspectionId),
      () => super.observeByInspection(divisionId, inspectionId),
    );
  }

  override search(divisionId: string, sQuery: TypedDesQuery, forceNoteInspectionId?: string): Observable<SearchResult<Issue>> {
    // A snapshot is immutable, so a one-shot emission is the honest shape here: there is no live
    // document to keep listening to.
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.issueSearch(payload, sQuery),
      () => super.search(divisionId, sQuery, forceNoteInspectionId),
    );
  }
}

@Injectable()
export class HistoryAwareAssetService extends FirebaseAssetService {
  private readonly history = inject(HistoricalViewState);

  override get(division: string, id: string | number): Promise<Asset> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.asset(payload, id),
      () => super.get(division, id),
    );
  }

  // Also covers the base class's getPreviousAsset(), which is built on search().
  override search(divisionId: string, query: Query): Promise<SearchResult<Asset>> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.assetSearch(payload, query),
      () => super.search(divisionId, query),
    );
  }
}

@Injectable()
export class HistoryAwareUprightService extends FirebaseUprightService {
  private readonly history = inject(HistoricalViewState);

  override get(division: string, id: string): Promise<Upright> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.upright(payload, id),
      () => super.get(division, id),
    );
  }

  override list(division: string, systemId: string): Promise<Upright[]> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.uprights(payload, systemId),
      () => super.list(division, systemId),
    );
  }

  override listColumns(division: string, assetIds: string[], rowIndex?: number): Promise<Column[]> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.columns(payload, assetIds, rowIndex),
      () => super.listColumns(division, assetIds, rowIndex),
    );
  }
}

@Injectable()
export class HistoryAwareSystemDrawingService extends FirebaseSystemDrawingService {
  private readonly history = inject(HistoricalViewState);
  private loadedGeneration: number = null;

  override get(division: string, system: string): Promise<System2d> {
    return readFromHistory(
      this.history,
      division,
      (payload) => snapshotReads.system(payload, system),
      () => super.get(division, system),
    );
  }

  /**
   * The base caches by division in a signal and only reloads on a key miss, so switching between
   * current and historical for the same warehouse would otherwise serve the other source's list.
   */
  override async list(division: string, section: string | null = null): Promise<System2d[]> {
    if (this.loadedGeneration !== this.history.generation) {
      this.loadedGeneration = this.history.generation;
      this.divisionId.set(division);
      this.section.set(section);
      await this.reloadSystems();

      return this.systems();
    }

    return super.list(division, section);
  }

  override reloadSystems(): Promise<void> {
    return readFromHistory(
      this.history,
      this.divisionId(),
      (payload) => this.systems.set(snapshotReads.systems(payload, this.section())),
      () => super.reloadSystems(),
    );
  }
}

@Injectable()
export class HistoryAwareWarehouseOutlineService extends FirebaseWarehouseOutlineService {
  private readonly history = inject(HistoricalViewState);

  override search(divisionId: string): Promise<SearchResult<WarehouseOutlineSegment>> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.outlineSearch(payload),
      () => super.search(divisionId),
    );
  }
}

@Injectable()
export class HistoryAwareWarehouseLayoutTextService extends FirebaseWarehouseLayoutTextService {
  private readonly history = inject(HistoricalViewState);

  override search(divisionId: string): Promise<SearchResult<LayoutTextSegment>> {
    return readFromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.layoutTextSearch(payload),
      () => super.search(divisionId),
    );
  }
}

@Injectable()
export class HistoryAwareWarehouseService extends FirebaseWarehouseService {
  private readonly history = inject(HistoricalViewState);
  private loadedGeneration: number = null;

  /**
   * The base caches outlines and texts by division only, so switching between current and
   * historical for the same warehouse would otherwise serve the other source's layout.
   */
  override async resolveOutline(divisionId: string): Promise<WarehouseOutlineSegment[]> {
    this.invalidateOnSourceChange();

    return super.resolveOutline(divisionId);
  }

  override async resolveLayoutTexts(divisionId: string): Promise<LayoutTextSegment[]> {
    this.invalidateOnSourceChange();

    return super.resolveLayoutTexts(divisionId);
  }

  override reloadOutlines(): Promise<void> {
    return reloadLayoutFromHistory(
      this.history,
      this.divisionId,
      (payload: WarehouseStateSnapshotPayload) => (this.outlines = snapshotReads.outline(payload)),
      () => super.reloadOutlines(),
      () => this.reloadOutlines(),
    );
  }

  override reloadLayoutTexts(): Promise<void> {
    return reloadLayoutFromHistory(
      this.history,
      this.divisionId,
      (payload: WarehouseStateSnapshotPayload) => (this.texts = snapshotReads.layoutTexts(payload)),
      () => super.reloadLayoutTexts(),
      () => this.reloadLayoutTexts(),
    );
  }

  private invalidateOnSourceChange(): void {
    if (this.loadedGeneration !== this.history.generation) {
      this.loadedGeneration = this.history.generation;
      this.invalidateResolvedLayout();
    }
  }
}

@Injectable()
export class HistoryAwareObservableAssetService extends FirebaseObservableAssetService {
  private readonly history = inject(HistoricalViewState);

  override observeById(divisionId: string, id: string): Observable<ChangeTyped<Asset>> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.assetObservation(payload, id),
      () => super.observeById(divisionId, id),
    );
  }

  override search(divisionId: string, sQuery: TypedDesQuery, forceNoteInspectionId?: string): Observable<SearchResult<Asset>> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.assetSearch(payload, sQuery),
      () => super.search(divisionId, sQuery, forceNoteInspectionId),
    );
  }
}

@Injectable()
export class HistoryAwareObservableNoteService extends FirebaseObservableNoteService {
  private readonly history = inject(HistoricalViewState);

  override observeDivisionNotes(divisionId: string): Observable<Note[]> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.divisionNotes(payload),
      () => super.observeDivisionNotes(divisionId),
    );
  }

  override observeNotes(divisionId: string, inspectionId: string, assetId?: string): Observable<Note[]> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.notes(payload, inspectionId, assetId),
      () => super.observeNotes(divisionId, inspectionId, assetId),
    );
  }

  override observeIssueNotes(divisionId: string, issueId: string): Observable<Note[]> {
    return fromHistory(
      this.history,
      divisionId,
      (payload) => snapshotReads.issueNotes(payload, issueId),
      () => super.observeIssueNotes(divisionId, issueId),
    );
  }
}

@Injectable()
export class HistoryAwareObservableIssueMediaService extends FirebaseObservableIssueMediaService {
  private readonly history = inject(HistoricalViewState);

  override observeMediaByIssue(division: string, issueId: string): Observable<ChangeTyped<IssueMedia>[]> {
    return fromHistory(
      this.history,
      division,
      (payload) => snapshotReads.issueMedia<IssueMedia>(payload, issueId),
      () => super.observeMediaByIssue(division, issueId),
    );
  }
}
